import React from 'react';
import { createPortal } from 'react-dom';
import ConfirmDialog from './ConfirmDialog';
import { useDepartment } from '../contexts/DepartmentContext';
import { useToast } from '../contexts/ToastContext';
import { ROLE_LABELS } from '../constants/statuses';
import useSubmitGuard from '../hooks/useSubmitGuard';

/**
 * "Join <Dept>?" — the prompt for a PENDING department invite
 * (join-a-second-department, P5, 2026-09-23).
 *
 * Owner ruling R16: *"… they get the pop up asking them if they want to join?
 * The question should be asked every time they click on the department and
 * they shouldnt be allowed in until they click accept/join/agree etc"*.
 * R21: there is NO Decline anywhere — the two answers are **Join** and
 * **Not now**.
 *
 * 🔑 NOT NOW CHANGES NOTHING. It closes the prompt and that is all: no storage
 * write, no department switch, no request. The invite stays in the switcher,
 * so the next click asks again — which is exactly what R16 asks for. The
 * caller mounts this component only while an invite is chosen, so "asked
 * every time" needs no remembered state at all.
 *
 * 🔑 JOIN goes through the context's `acceptInvite`, which lands the user in
 * the new department through the ONE landing rule (`chooseActiveDepartment`)
 * rather than a second writer of the active department. On a refusal the
 * server's own message is shown and the invite list is refreshed; 410 (no
 * longer valid) and 404 (gone) close the prompt, anything else (a network
 * failure, a 500) leaves it open so the user can retry. The accept's one 409,
 * `invite_changed`, re-shows the NEW terms and waits for a fresh Join
 * (`handleInviteChanged`). 🔻 A 409 `name_clash_in_department` also closed
 * the prompt until task NC (2026-09-24) made a name clash join anyway — the
 * server no longer produces it; branch deleted, pre-deletion SHA `df544f5e`.
 *
 * Mounted by TWO callers: `ProductionDepartmentSwitcher.js` (a pending row)
 * and `Layout.js`'s `NotificationBell` (a `department_invite` notification).
 * Both run `hasDirtyEdit()` BEFORE mounting this, with the copy below.
 *
 * No colour classes on the chrome: `ConfirmDialog` owns it and its buttons come
 * from `constants/buttons.js`. The body's own text colours carry `dark:` pairs
 * (a shared component owns its own text colour — `components/CLAUDE.md`).
 */

// The unsaved-changes wording, owner-verbatim (2026-08-30 — see
// `ProductionDepartmentSwitcher.js`). Exported so the switcher and the bell show
// ONE sentence: joining lands you in another department, so it discards an
// unsaved order edit exactly as a switch does.
export const UNSAVED_SWITCH_TITLE = 'Unsaved changes';
export const UNSAVED_SWITCH_MESSAGE = "You have unsaved changes — they'll be lost.";

/**
 * Render a modal at `document.body`, out of whatever stacking context its
 * caller sits in.
 *
 * 🚨 MEASURED 2026-09-23 (P5 browser evidence, 1440px, real build): the
 * desktop sidebar wraps the switcher in `relative z-30`, which is a STACKING
 * CONTEXT, and the page's sticky title bar in `<main>` is ALSO `z-30` and
 * later in the DOM — so `ConfirmDialog`'s `fixed inset-0 z-50` overlay, opened
 * from the sidebar switcher, painted UNDER the title bar: the bell stayed
 * clickable above a "modal". `z-50` cannot win from inside a `z-30` context;
 * only leaving the context can (`components/ui/CLAUDE.md`'s z-index contract:
 * "check whether the panel is behind"). The switcher's pre-existing My Space
 * and unsaved-changes dialogs had the same defect and use this too.
 */
export function toBody(node) {
  if (typeof document === 'undefined' || !document.body) return node;
  return createPortal(node, document.body);
}

// Codes meaning the invite no longer EXISTS: the prompt closes and `onGone`
// fires — the bell uses it to mark its notification read ("bell keeps it"
// until the invite ends). Every other refusal leaves the prompt open.
const GONE_STATUSES = new Set([404, 410]);

export function joinDialogTitle(invite) {
  return `Join ${invite?.department_name || 'this department'}?`;
}

function Row({ label, value, testid }) {
  if (!value) return null;
  return (
    <div className="flex gap-2">
      <dt className="w-24 shrink-0 text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="min-w-0 font-medium text-gray-800 dark:text-gray-100 break-words" data-testid={testid}>{value}</dd>
    </div>
  );
}

export const INVITE_CHANGED_MESSAGE = 'This invite was changed — please review it again';

export default function JoinDepartmentDialog({ invite: initialInvite, onClose, onGone, returnFocusRef }) {
  const { acceptInvite, refreshPendingInvites } = useDepartment();
  const toast = useToast();
  // THE house double-submit guard (hooks/useSubmitGuard.js): a REF armed
  // before the await, so two clicks with no render between them send ONE
  // accept. This was `setBusy(true)` — a state flag, which the second click
  // of such a pair still read as false (addendum B, 2026-09-24).
  const { guard, busy } = useSubmitGuard();

  // Focus return (addendum C). `ConfirmDialog` restores focus to what was
  // focused when it OPENED — the pending row / notification, which unmounts in
  // that same commit — so on close focus fell to <body>. After this dialog
  // unmounts (a microtask, i.e. after ConfirmDialog's own restore), move focus
  // to the opener's trigger, but only when nothing else has taken it.
  const returnFocusRefLatest = React.useRef(returnFocusRef);
  returnFocusRefLatest.current = returnFocusRef;
  React.useEffect(() => () => {
    queueMicrotask(() => {
      const target = returnFocusRefLatest.current && returnFocusRefLatest.current.current;
      const active = document.activeElement;
      if (target && typeof target.focus === 'function' && document.contains(target)
        && (!active || active === document.body)) {
        target.focus();
      }
    });
  }, []);
  // The invite AS SHOWN. Replaced (never merged) when the server answers
  // `invite_changed`, so what is on screen is always what Join consents to.
  const [invite, setInvite] = React.useState(initialInvite);
  const [notice, setNotice] = React.useState(null);

  if (!invite) return null;

  const deptName = invite.department_name || 'this department';
  const roleLabel = ROLE_LABELS[invite.role] || invite.role;

  // 409 `invite_changed` (review R3): the manager edited the invite after it
  // was shown. Refresh, re-show the NEW terms, say so, and wait for a fresh
  // Join — never accept on the user's behalf at terms they did not see.
  const handleInviteChanged = async () => {
    const r = await refreshPendingInvites?.();
    if (!r || r.ok !== true) {
      toast.error('Couldn’t load your invites — try again');
      return;
    }
    const fresh = (r.invites || []).find(i => String(i.id) === String(invite.id));
    if (!fresh) {
      toast.info('That invitation is no longer open.');
      onGone?.();
      onClose?.();
      return;
    }
    setInvite(fresh);
    setNotice(INVITE_CHANGED_MESSAGE);
  };

  // The gate releases when this promise settles — success (the prompt closes),
  // a closing refusal, or a retryable failure (the prompt stays, Join enabled).
  // 🔑 The guarded promise resolves on EVERY path here, never rejects: every
  // branch below is inside this try/catch, including the `invite_changed`
  // arm's call to `handleInviteChanged`, whose only await
  // (`refreshPendingInvites`) resolves `{ ok, invites }` and never rejects
  // (DepartmentContext's own contract). `useSubmitGuard` does not require
  // this — a rejection re-enables the guard just as a resolution does
  // (`useSubmitGuard.test.js` §1.2 covers that arm) — it simply never fires
  // from this caller.
  const handleJoin = () => guard(async () => {
    try {
      const joined = await acceptInvite(invite.id, invite.role);
      // `already_member: true` (a seat already existed; the invite is accepted
      // and no seat written) is not a join — say what is true (observation O2).
      toast.success(joined && joined.already_member === true
        ? `You're already a member of ${deptName}`
        : `You've joined ${deptName}`);
      onClose?.();
    } catch (err) {
      if (err && err.code === 'invite_changed') {
        await handleInviteChanged();
        return;
      }
      const message = (err && err.message) || 'Could not join this department.';
      toast.error(message);
      if (err && GONE_STATUSES.has(err.status)) {
        onGone?.();
        refreshPendingInvites?.();
        onClose?.();
      }
    }
  });

  return toBody(
    <ConfirmDialog
      open
      title={joinDialogTitle(invite)}
      message={(
        <div data-testid="join-dept-dialog">
          {notice && (
            <p
              role="status"
              data-testid="join-dept-changed-notice"
              className="mb-3 rounded-md px-3 py-2 bg-amber-50 text-amber-800 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-700"
            >
              {notice}
            </p>
          )}
          <dl className="space-y-1 mb-3">
            <Row label="Department" value={invite.department_name} testid="join-dept-department" />
            <Row label="Production" value={invite.production_name} testid="join-dept-production" />
            <Row label="Role" value={roleLabel} testid="join-dept-role" />
            <Row label="Invited by" value={invite.invited_by_name} testid="join-dept-inviter" />
          </dl>
          {/* A hairline + gap between the facts and the privacy sentence
              (owner, 2026-09-23): the sentence is a consequence, not a fact. */}
          <p className="pt-3 border-t border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300" data-testid="join-dept-contact-note">
            {`Once you join, the managers of ${deptName} will be able to see your contact details.`}
          </p>
        </div>
      )}
      confirmLabel="Join"
      cancelLabel="Not now"
      busy={busy}
      onConfirm={handleJoin}
      onCancel={() => onClose?.()}
    />
  );
}
